/**
 * Field form nguồn khách hàng (catalog). Dùng trong FormProvider.
 */
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CharacterCount,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@/shared/ui';
import {
  CUSTOMER_SOURCE_NAME_MAX_LENGTH,
  type CustomerSourceFormValues,
} from '../utils/customerSourceForm';

const selectIdleIconSx = {
  '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
  '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
};

const CatalogStatusOption = ({ active, label }: { active: boolean; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box
      aria-hidden
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: active ? 'success.main' : 'text.disabled',
        flexShrink: 0,
      }}
    />
    {label}
  </Box>
);

export interface CustomerSourceFormFieldsProps {
  isEdit: boolean;
  /** Mã nguồn (chỉ hiển thị khi sửa). */
  code?: string;
  disabled?: boolean;
}

const CustomerSourceFormFields: React.FC<CustomerSourceFormFieldsProps> = ({
  isEdit,
  code,
  disabled = false,
}) => {
  const { t } = useTranslation('patient');
  const { control } = useFormContext<CustomerSourceFormValues>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {isEdit && code ? (
        <TextField
          fullWidth
          disabled
          label={t('customerSourceCatalog.table.code')}
          value={code}
        />
      ) : null}

      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            autoFocus={!isEdit}
            fullWidth
            disabled={disabled}
            label={t('customerSourceCatalog.form.name')}
            placeholder={t('customerSourceCatalog.placeholders.name')}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            labelEnd={
              <CharacterCount current={field.value.length} max={CUSTOMER_SOURCE_NAME_MAX_LENGTH} />
            }
            slotProps={{
              htmlInput: { maxLength: CUSTOMER_SOURCE_NAME_MAX_LENGTH },
            }}
          />
        )}
      />

      <Grid container spacing={2}>
        <Grid size={isEdit ? 6 : 12}>
          <Controller
            name="sortOrder"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="number"
                disabled={disabled}
                label={t('customerSourceCatalog.fields.sortOrder')}
                placeholder={t('customerSourceCatalog.placeholders.sortOrder')}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === '') {
                    field.onChange(0);
                    return;
                  }
                  const parsed = Number(raw);
                  field.onChange(Number.isFinite(parsed) ? parsed : 0);
                }}
                value={field.value}
              />
            )}
          />
        </Grid>
        {isEdit ? (
          <Grid size={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth disabled={disabled} sx={selectIdleIconSx}>
                  <InputLabel>{t('customerSourceCatalog.fields.status')}</InputLabel>
                  <Select
                    label={t('customerSourceCatalog.fields.status')}
                    value={field.value ? 'active' : 'inactive'}
                    onChange={(e) => field.onChange(e.target.value === 'active')}
                    renderValue={(selected) => (
                      <CatalogStatusOption
                        active={selected === 'active'}
                        label={
                          selected === 'active'
                            ? t('customerSourceCatalog.status.active')
                            : t('customerSourceCatalog.status.inactive')
                        }
                      />
                    )}
                  >
                    <MenuItem value="active">
                      <CatalogStatusOption active label={t('customerSourceCatalog.status.active')} />
                    </MenuItem>
                    <MenuItem value="inactive">
                      <CatalogStatusOption
                        active={false}
                        label={t('customerSourceCatalog.status.inactive')}
                      />
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        ) : (
          <Grid size={12}>
            <FormControl fullWidth disabled sx={selectIdleIconSx}>
              <InputLabel>{t('customerSourceCatalog.fields.status')}</InputLabel>
              <Select
                value="active"
                label={t('customerSourceCatalog.fields.status')}
                renderValue={() => (
                  <CatalogStatusOption active label={t('customerSourceCatalog.status.active')} />
                )}
              >
                <MenuItem value="active">
                  <CatalogStatusOption active label={t('customerSourceCatalog.status.active')} />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CustomerSourceFormFields;
